/**
 * Batching and serve-size scaling (#69): the arithmetic a bartender does by
 * hand before service, where a slip costs a whole container.
 *
 * Two different questions, kept as two functions.
 *
 * **A batch** is the build multiplied up to fill a container, or to make so
 * many serves, and read out in what somebody actually pours: two bottles and
 * 140 mL, not 1,540 mL. Dilution is the one line that does not scale like
 * the others. A shaken drink is diluted on the night, over ice, so its water
 * stays out of the batch; a stirred drink bottled ready to pour has its water
 * added in the batch. Which it is, is the bartender's call (`withWater`).
 *
 * **A serve size** is the same drink in a bigger or smaller glass: every line
 * scaled by the same factor, so the ABV holds and the cost, standard drinks
 * and GP follow. That is `scaleLines` and the costing that already exists.
 *
 * Nothing here is saved. A batch is worked out when it is made.
 */
import { bottleSpec } from "./stock.js";
/**
 * Kinds that come in a bottle somebody counts whole. The rest — juice, syrup,
 * the house mixers — are made or decanted, and splitting them into bottles
 * would be inventing a container.
 */
const BOTTLED = new Set(["spirit", "liqueur", "fortified"]);
/**
 * Every line multiplied by `factor`. Millilitres scale; so does the count of
 * a dash or a drop, so a doubled drink reads "2 dash" rather than keeping the
 * single dash's wording with twice its volume.
 */
export function scaleLines(lines, factor) {
    const f = Number.isFinite(factor) && factor > 0 ? factor : 0;
    return lines.map((line) => ({
        ...line,
        ml: line.ml * f,
        ...(line.qty !== undefined ? { qty: line.qty * f } : {}),
    }));
}
/** A bottle's worth, and what is left over. Nothing to split under a bottle. */
export function inBottles(ml, bottleMl) {
    const whole = Math.round(ml);
    if (!bottleMl || bottleMl <= 0 || whole < bottleMl)
        return { bottles: 0, remainderMl: whole };
    const bottles = Math.floor(whole / bottleMl);
    return { bottles, remainderMl: whole - bottles * bottleMl };
}
/**
 * A batch of a build.
 *
 * The volume target counts only what goes in the container: with the water
 * left out, 3 L of a stirred drink is 3 L of spirit and vermouth, and the
 * water is added per serve at the bar. `serves` is then however many that
 * makes, which is the number a bartender checks against the night.
 */
export function batch(lines, ingredients, target, withWater) {
    const isWater = (id) => ingredients.get(id)?.kind === "dilution";
    const batched = lines.filter((l) => withWater || !isWater(l.ingredientId));
    const perServeMl = batched.reduce((sum, l) => sum + l.ml, 0);
    const dilutionPerServeMl = withWater
        ? 0
        : lines.filter((l) => isWater(l.ingredientId)).reduce((sum, l) => sum + l.ml, 0);
    const factor = perServeMl <= 0
        ? 0
        : target.kind === "volume"
            ? Math.max(0, target.ml) / perServeMl
            : Math.max(0, target.count);
    // Lines that are the same ingredient twice — a split base, a rinse and a
    // measure — are one pour in a batch.
    const totals = new Map();
    for (const line of batched)
        totals.set(line.ingredientId, (totals.get(line.ingredientId) ?? 0) + line.ml * factor);
    // Whole millilitres that still add up to the batch: rounding a 1 L batch
    // of three equal parts line by line gives 999 mL, so the spare millilitres
    // go to the lines that lost most to rounding down.
    const exact = [...totals.entries()];
    const floors = exact.map(([, ml]) => Math.floor(ml));
    let spare = Math.round(exact.reduce((sum, [, ml]) => sum + ml, 0)) - floors.reduce((a, b) => a + b, 0);
    const byLoss = exact.map(([, ml], i) => ({ i, loss: ml - Math.floor(ml) })).sort((a, b) => b.loss - a.loss);
    for (const { i } of byLoss) {
        if (spare <= 0)
            break;
        floors[i] = floors[i] + 1;
        spare -= 1;
    }
    const out = exact.map(([ingredientId], i) => {
        const water = isWater(ingredientId);
        const ingredient = ingredients.get(ingredientId);
        // The stocktake's bottle size, so a batch and a count agree on what a
        // bottle is: the ingredient's own, or 700 mL.
        const bottleMl = !water && ingredient && (BOTTLED.has(ingredient.kind) || (ingredient.bottleMl ?? 0) > 0)
            ? bottleSpec(ingredient).bottleMl
            : null;
        const ml = floors[i];
        return { ingredientId, ml, ...inBottles(ml, bottleMl), bottleMl, water };
    });
    return {
        factor,
        serves: factor,
        volumeMl: out.reduce((sum, l) => sum + l.ml, 0),
        lines: out,
        dilutionPerServeMl,
    };
}
//# sourceMappingURL=batch.js.map